/**
 * Kingdom Minded — header behaviour.
 * Drawer open/close with focus trap, scroll-lock, and a stuck-state class
 * so the transparent homepage header gains its background once scrolled.
 */
/**
 * Horizon does not scroll the window: `html` and `body` are `overflow:hidden`
 * and `.page-wrapper` is the scroll container. Listening on `window` would never
 * fire, and locking `documentElement.overflow` would never lock anything — so
 * both the scroll listener and the drawer scroll-lock target that element.
 */
function getScroller() {
  return document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
}

class KMHeader extends HTMLElement {
  connectedCallback() {
    this.scroller = getScroller();
    this.burger = this.querySelector('.km-header__burger');
    this.drawer = this.querySelector('.km-header__drawer');
    this.scrim = this.querySelector('.km-header__scrim');
    this.closeBtn = this.querySelector('.km-header__close');

    if (this.burger && this.drawer) {
      this.burger.addEventListener('click', () => this.toggle());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.scrim?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', this.onKeydown);
      // Any navigation from inside the drawer should close it.
      this.drawer.addEventListener('click', (e) => {
        if (e.target.closest('a')) this.close();
      });
    }

    this.onScroll();
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    // Belt and braces: if a future layout scrolls the window instead, still respond.
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.scroller?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll = () => {
    const y = this.scroller === window ? window.scrollY : this.scroller.scrollTop;
    this.classList.toggle('is-stuck', y > 24);
  };

  onKeydown = (e) => {
    if (!this.isOpen) return;
    if (e.key === 'Escape') {
      this.close();
      return;
    }
    if (e.key !== 'Tab') return;

    const focusables = this.drawer.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  get isOpen() {
    return this.drawer && !this.drawer.hidden;
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.lastFocused = document.activeElement;
    this.drawer.hidden = false;
    this.scrim.hidden = false;
    // Force a frame so the transform transition runs from its start value.
    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
      this.scrim.classList.add('is-open');
    });
    this.burger.setAttribute('aria-expanded', 'true');
    this.burger.setAttribute('aria-label', 'Close menu');
    this.scroller.style.overflow = 'hidden';
    this.drawer.querySelector('a, button')?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.scrim.classList.remove('is-open');
    this.burger.setAttribute('aria-expanded', 'false');
    this.burger.setAttribute('aria-label', 'Open menu');
    this.scroller.style.overflow = '';

    const done = () => {
      this.drawer.hidden = true;
      this.scrim.hidden = true;
      this.drawer.removeEventListener('transitionend', done);
    };
    this.drawer.addEventListener('transitionend', done);
    // Fallback in case the transition never fires (reduced motion, hidden tab).
    setTimeout(done, 400);

    this.lastFocused?.focus();
  }
}

if (!customElements.get('km-header')) {
  customElements.define('km-header', KMHeader);
}

/**
 * Kingdom Minded — primary nav dropdowns (Shop, Volumes).
 *
 * Desktop supports hover AND keyboard. The trigger stays a real link: click and
 * Enter navigate to the Shop/Volumes page as they always did, while Space or
 * Arrow Down opens the panel. Nothing here hijacks Enter from a link.
 *
 * The panel is a child of its <li>, and the <li> is what listens for pointer
 * enter/leave — so travelling from the nav label down into the panel never
 * crosses outside the listening element and the menu cannot close mid-move.
 * A close delay covers diagonal travel.
 *
 * Below 860px the panels are display:none and the drawer accordions take over,
 * so none of the hover logic binds.
 */
(function () {
  'use strict';

  const DESKTOP = '(min-width: 861px)';
  const CLOSE_DELAY = 220;

  function panelLinks(panel) {
    return Array.from(panel.querySelectorAll('a[href]'));
  }

  function initItem(item, group) {
    const trigger = item.querySelector('[data-km-nav-trigger]');
    const panel = item.querySelector('[data-km-nav-panel]');
    if (!trigger || !panel) return null;

    let closeTimer = null;

    const api = {
      item,
      isOpen: () => item.classList.contains('is-open'),
      open() {
        clearTimeout(closeTimer);
        group.forEach((other) => {
          if (other !== api && other.isOpen()) other.close();
        });
        panel.hidden = false;
        /*
          Flush layout so the fade has a start frame to run from. Deliberately a
          forced reflow rather than requestAnimationFrame: rAF is throttled - and
          in an occluded or background tab simply never fires - which would leave
          the panel un-hidden but permanently transparent. Correctness of the open
          state matters more here than the 180ms fade.
        */
        void panel.offsetHeight;
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      },
      close() {
        clearTimeout(closeTimer);
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        // Keep it in the a11y tree until the fade finishes, then remove it.
        closeTimer = setTimeout(() => {
          if (!item.classList.contains('is-open')) panel.hidden = true;
        }, 200);
      },
      closeSoon() {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => api.close(), CLOSE_DELAY);
      },
      focusTrigger() {
        trigger.focus();
      },
    };

    item.addEventListener('mouseenter', () => api.open());
    item.addEventListener('mouseleave', () => api.closeSoon());

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Spacebar') {
        // Space would otherwise scroll the page; Enter is left alone so the
        // link still navigates.
        e.preventDefault();
        api.open();
        panelLinks(panel)[0]?.focus();
      } else if (e.key === 'Escape') {
        api.close();
      }
    });

    panel.addEventListener('keydown', (e) => {
      const links = panelLinks(panel);
      const i = links.indexOf(document.activeElement);

      if (e.key === 'Escape') {
        e.preventDefault();
        api.close();
        api.focusTrigger();
      } else if (e.key === 'ArrowDown' && i > -1) {
        e.preventDefault();
        links[(i + 1) % links.length].focus();
      } else if (e.key === 'ArrowUp' && i > -1) {
        e.preventDefault();
        links[(i - 1 + links.length) % links.length].focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        links[0]?.focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        links[links.length - 1]?.focus();
      }
    });

    // Tabbing out of the item in either direction closes it.
    item.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!item.contains(document.activeElement)) api.close();
      });
    });

    return api;
  }

  function initDrawerToggles(root) {
    root.querySelectorAll('[data-km-drawer-toggle]').forEach((btn) => {
      if (btn.dataset.kmBound === 'true') return;
      btn.dataset.kmBound = 'true';

      const sub = document.getElementById(btn.getAttribute('aria-controls'));
      if (!sub) return;

      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        sub.hidden = open;
      });
    });
  }

  function boot() {
    const items = Array.from(document.querySelectorAll('[data-km-nav-item]'));
    const group = [];

    if (window.matchMedia(DESKTOP).matches) {
      items.forEach((item) => {
        const api = initItem(item, group);
        if (api) group.push(api);
      });

      document.addEventListener('click', (e) => {
        group.forEach((api) => {
          if (api.isOpen() && !api.item.contains(e.target)) api.close();
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        group.forEach((api) => {
          if (!api.isOpen()) return;
          api.close();
          api.focusTrigger();
        });
      });
    }

    const drawer = document.getElementById('km-nav-drawer');
    if (drawer) initDrawerToggles(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
